import { Location } from '@angular/common';
import { Component, OnDestroy, OnInit, inject, input, ChangeDetectionStrategy } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { safeUTC } from '@app/extensions/moment-extensions';
import { GetAllActiveStudiesResponse, StudyPaymentApprovalThresholdControllerV2Service } from '@app/generated-api';
import { MessageDialogComponent } from '@app/v2/components/dialogs/message-dialog/message-dialog.component';
import { StudyPaymentApprovalThresholdFormGroup } from '@app/v2/components/study-payment-approval-threshold-card/study-payment-approval-threshold-card.form';
import { ClinTrakDataService2 } from '@app/v2/services/clintrak/clintrak-data.service';
import { StudyServiceV2 } from '@app/v2/services/study/study.service';
import { UserServiceV2 } from '@app/v2/services/user-service/user.service';
import { AccessGroupEnum } from '@models/accessGroup';
import { ClinTrakPersonViewModel, CountryRegionCtsmViewModel } from '@models/clintrak';
import { StudySaveType } from '@models/event-objects/study-save-type';
import { VisitGroup } from '@models/visitGroup';
import { AdminSiteServices } from '@services/admin/admin-site.service';
import { ClinTrakDataService } from '@services/clintrak/clintrak-data.service';
import { CurrentlyEditedCardService } from '@services/currently-edited-card/currently-edited-card.service';
import { SnackbarService } from '@services/snackbar/snackbar.service';
import { ClinTrakDataStateService } from '@services/state-management/clintrak-data-state.service';
import { SiteStateService } from '@services/state-management/site-state.service';
import { StudyStateService } from '@services/state-management/study-state.service';
import { transformToSiteDataModel } from '@services/transforms/site-transform';
import { ModalSize } from '@utility/modal-helper';
import { markFormGroupTouched } from '@utility/utility';
import { Subject, combineLatest, finalize, switchMap, take, takeUntil, tap } from 'rxjs';
import { User } from 'src/app/models/user';
import { Site, createNewSite } from '../../models/site';
import {
    CreateUpdateStudyDataModel,
    Study,
    StudyDataModel,
    StudyExpense,
    StudyInformation,
    StudyMappedVisitDto,
    StudyRegion,
    StudyTravelOption,
    VisitName,
} from '../../models/study';
import { AdminStudyServices } from '../../services/admin/admin-study.sevice';
import {
    transformToCreateUpdateStudyDataModel,
    transformToStudyDataModel,
} from '../../services/transforms/study-transform';
import { MedpaceBulkSiteModalComponent } from '../modals/bulk-site/site-upload-modal.component';
import { MedpaceNewSiteModalComponent } from '../modals/new-site/new-site-modal.component';
import { disableFormEffect } from '@app/v2/utils/signal-utils';

@Component({
    selector: '',
    standalone: false,
    templateUrl: './new-edit-study.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./new-edit-study.component.scss'],
})
export class StudyComponent implements OnInit, OnDestroy {
    id = input.required<number, string>({ transform: (value) => Number(value) });
    generatedStudyV2Service = inject(StudyPaymentApprovalThresholdControllerV2Service);
    studyThresholds = rxResource({
        params: () => {
            const studyId = this.id();
            if (!studyId) return undefined;

            return { studyId };
        },
        stream: (params) =>
            this.generatedStudyV2Service.studyPaymentApprovalThresholdControllerV2GetThresholds(params.params.studyId),
    });
    studyThresholdsFormGroup = new StudyPaymentApprovalThresholdFormGroup();
    studyThresholdsPermissions = {
        read: this.userService.signalPermissions.study.paymentApprovalThresholds.read,
        edit: this.userService.signalPermissions.study.paymentApprovalThresholds.edit,
    };

    disableStudyThresholdsForm = disableFormEffect({
        form: () => this.studyThresholdsFormGroup,
        disabled: () => !this.studyThresholdsPermissions.edit(),
    });

    study: Study;
    isEditStudy: boolean = false;
    studyUsers: User[];
    clinTrakStudyGuid: string;
    clintrakContext: boolean = true;
    private componentDestroyed$: Subject<boolean> = new Subject();

    private readonly studyServicev2 = inject(StudyServiceV2);
    private readonly clintrakServiceV2 = inject(ClinTrakDataService2);
    private readonly clinTrakDataStateService = inject(ClinTrakDataStateService);

    isAssistantOrHigher$ = this.userService.isRole$([
        AccessGroupEnum.Manager,
        AccessGroupEnum.Lead_Coordinator,
        AccessGroupEnum.Coordinator,
        AccessGroupEnum.Assistant,
    ]);

    constructor(
        private studyServices: AdminStudyServices,
        private activeRoute: ActivatedRoute,
        private userService: UserServiceV2,
        private router: Router,
        private snackbarService: SnackbarService,
        private dialog: MatDialog,
        private stateService: StudyStateService,
        private siteStateService: SiteStateService,
        private adminSiteServices: AdminSiteServices,
        private cardService: CurrentlyEditedCardService,
        private location: Location,
        private clinTrakDataService: ClinTrakDataService
    ) {}

    ngOnInit() {
        // clear store on page load to ensure no data from previous study is shown while loading new study, this can be removed when we move to signals and each component only subscribes to the data it needs
        this.clearStore();
        if (this.activeRoute.snapshot.routeConfig.path !== 'admin/newstudy' && (history.state as Study)['id']) {
            this.isEditStudy = true;
            this.study = history.state;

            this.setStates(history.state.id);
            this.getStates();
        } else if (this.activeRoute.snapshot.routeConfig.path !== 'admin/newstudy') {
            this.isEditStudy = true;
            this.setStates(this.activeRoute.snapshot.params.id);
            this.getStates();
        }

        if (this.activeRoute.snapshot.routeConfig.path === 'admin/newstudy') {
            this.isEditStudy = false;
            this.study = new Study();
            this.study.studyInfo = new StudyInformation();
            this.study.studyInfo.visitTypes = [
                <VisitName>{
                    name: '',
                    sortOrder: 1,
                    visitGroup: <VisitGroup>{ name: 'Visit schedule', sortOrder: 1 },
                },
            ];
            this.study.sites = [];
            this.study.supportTier = 1;

            const clinTrakStudy: GetAllActiveStudiesResponse =
                this.router.lastSuccessfulNavigation()?.extras?.state?.event;
            if (clinTrakStudy) {
                return this.setDataFromClinTrak(clinTrakStudy);
            } else this.clintrakContext = false;

            this.stateService.setNewStudy(this.study);
        }
    }

    private setDataFromClinTrak(clinTrakStudy: GetAllActiveStudiesResponse) {
        if (!!clinTrakStudy.pcsId) {
            this.setStates(clinTrakStudy.pcsId);
            this.getStates();
        }
        this.clinTrakStudyGuid = clinTrakStudy.studyId;
        this.study.studyCode = clinTrakStudy.projectId;
        this.study.sponsor = clinTrakStudy.sponsorName;
        this.study.protocol = clinTrakStudy.studyNumber;
        this.study.isFromClinTrak = true;
        this.clintrakServiceV2
            .getClinTrakStudyData(clinTrakStudy.studyId)
            .pipe(
                switchMap((data) => {
                    this.clinTrakDataStateService.setStudyClinTrakData(data);
                    return this.stateService.getAvailableExpenses().pipe(
                        take(1), // must complete
                        tap((expenses) => {
                            if (!!clinTrakStudy.pcsId) return;
                            this.study.studyInfo.globalCtm =
                                data?.globalCtm?.map((p) => this.mapClinTrakPerson(p)) ?? [];
                            this.study.studyInfo.globalRsm =
                                data?.globalRsm?.map((p) => this.mapClinTrakPerson(p)) ?? [];
                            this.study.studyInfo.prm = data?.prm?.map((p) => this.mapClinTrakPerson(p)) ?? [];
                            this.study.studyInfo.regionalCtms =
                                data?.regionalCtms?.map((p) => this.mapClinTrakPerson(p)) ?? [];
                            this.study.studyInfo.pc = data?.pc?.map((p) => this.mapClinTrakPerson(p)) ?? [];
                            this.study.studyInfo.studyCountries =
                                data?.countries?.map((c) => this.mapClinTrakCountry(c)) ?? [];
                            this.study.studyInfo.numPatients = data?.numPatients;
                            this.study.studyInfo.numSites = data?.numSites;
                            this.study.studyInfo.expenseTypes = expenses.map(
                                (element) => <StudyExpense>{ ...element, active: true }
                            );

                            this.study.studyInfo.fpfv = data?.fpfv ? safeUTC(data?.fpfv) : undefined;
                            this.study.studyInfo.lplv = data?.lplv ? safeUTC(data?.lplv) : undefined;
                        }),
                        finalize(() => (!clinTrakStudy.pcsId ? this.stateService.setNewStudy(this.study) : null))
                    );
                })
            )
            .subscribe();
    }

    setStates(id: number) {
        this.stateService.setStudy(id);
        this.stateService.setUsers(id);

        this.stateService
            .getUsers()
            .pipe(take(1))
            .subscribe((users) => {
                this.siteStateService.setNewUsers(users);
            });
    }

    getStates() {
        let study$ = this.stateService.getStudy();
        let users$ = this.stateService.getUsers();
        combineLatest({ study: study$, users: users$ })
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe(({ study, users }) => {
                if (study) this.study = study;
                this.studyUsers = users;
            });
    }
    clearStore() {
        this.stateService.clearStore();
        this.siteStateService.clearStore();
    }
    ngOnDestroy() {
        this.clearStore();
        this.componentDestroyed$.next(true);
        this.componentDestroyed$.complete();
        this.clinTrakDataStateService.clearStudyClinTrakData();
    }

    checkEditStatus(): string {
        return this.isEditStudy ? 'Edit Study' : 'Create New Study';
    }
    uploadSpreadsheetClicked() {
        const dialogRef = this.dialog.open<MedpaceBulkSiteModalComponent, User[], Site[]>(
            MedpaceBulkSiteModalComponent,
            { id: 'UploadSpreadsheetDialog', disableClose: true, data: this.studyUsers }
        );
        dialogRef.afterClosed().subscribe((uploadedSites) => {
            // in case user cancels
            if (uploadedSites) {
                uploadedSites.forEach((site) => {
                    site.studyId = this.study.id;
                    this.study.sites.push(site);
                });
                this.updateExistingStudy({ type: 'incrementalSave', doRoute: false }, true);
            }
        });
    }
    addNewSiteClicked() {
        let site = createNewSite();
        site.id = 0;
        site.studyId = this.study.id;
        const dialogRef = this.dialog.open<MedpaceNewSiteModalComponent, { site: Site; studyUsers: User[] }, Site>(
            MedpaceNewSiteModalComponent,
            {
                id: 'NewSiteDialog',
                width: '80%',
                maxWidth: 800,
                minWidth: 360,
                disableClose: true,
                data: { site, studyUsers: this.studyUsers },
            }
        );
        dialogRef.afterClosed().subscribe((newSite) => {
            // in case user cancels
            if (newSite) {
                const mappedSite = transformToSiteDataModel(newSite);
                this.adminSiteServices
                    .createSite(mappedSite)
                    .pipe(takeUntil(this.componentDestroyed$))
                    .subscribe(() => {
                        this.setStates(mappedSite.studyId);
                        this.getStates();
                    });
            }
        });
    }
    siteRowClicked(clickedSite: Site) {
        this.router.navigate([`studies/${this.study.id}/sites/${clickedSite.id}`]).then((navigated: boolean) => {
            if (navigated) {
                this.snackbarService.openInfoSnackbar('Redirect to site ' + clickedSite.info.name);
            }
        });
    }

    registerLogoChange(event: string): void {
        this.study.logoUri = event;
    }

    registerStudyRegionChange(event: any): void {
        this.study.studyInfo.studyRegions = event;
    }

    registerStudyCountryChange(event: any): void {
        this.study.studyInfo.studyCountries = event;
    }

    registerStudyTravelOptionChange(event: any): void {
        if (!!this.study?.studyInfo?.studyTravelOptions) {
            this.study.studyInfo.studyTravelOptions = event;
        }
    }

    registerStudyInfoChange(event: StudyInformation) {
        if (!this.study?.studyInfo) {
            this.study.studyInfo = {
                globalCtm: undefined,
                pc: undefined,
                prm: undefined,
                regionalCtms: undefined,
                globalRsm: undefined,
                status: undefined,
                indication: undefined,
                numPatients: undefined,
                numSites: undefined,
                fpfv: undefined,
                lplv: undefined,
                visitTypes: <VisitName[]>[],
                expenseTypes: <StudyExpense[]>[],
                studyRegions: <StudyRegion[]>[],
                studyCountries: <CountryRegionCtsmViewModel[]>[],
                studyTravelOptions: <StudyTravelOption[]>[],
                studyMappedVisits: <StudyMappedVisitDto[]>[],
                hasMultipleVisitSchedules: undefined,
            };
        }

        this.study.studyInfo = { ...this.study.studyInfo, ...event };
    }

    registerStudyDetailsChange(event: any) {
        this.study = { ...this.study, ...event };
    }

    rejectClinTrakStudy() {
        this.clinTrakDataService
            .rejectStudy(this.clinTrakStudyGuid)
            .pipe(
                take(1),
                finalize(() => {
                    this.location.back();
                    this.snackbarService.openInfoSnackbar('Study rejected successfully');
                })
            )
            .subscribe();
    }

    submitNewStudy(event: any) {
        if (!this.studyThresholdsFormGroup.valid && !this.studyThresholdsFormGroup.disabled) {
            markFormGroupTouched(this.studyThresholdsFormGroup);
            return;
        }

        this.study.isActive = true;
        const model = transformToCreateUpdateStudyDataModel(this.study);
        model.isApproved = false;
        model.isFromClinTrak = true;

        const dirtyThresholdRows = this.studyThresholdsFormGroup.timelineFormGroups().filter((r) => r.isDirty());
        if (dirtyThresholdRows.length > 0) {
            model.paymentApprovalThresholds = dirtyThresholdRows.map((r) => ({
                threshold1: r.controls.threshold1.value ?? undefined,
                threshold2: r.controls.threshold2.value ?? undefined,
                effectiveDate: r.controls.effectiveDate.value!,
            }));
        }

        this.studyServices.createStudy(model).subscribe({
            next: (response) => {
                this.location.back();
                this.snackbarService.openInfoSnackbar('Study submitted successfully');
            },
            error: (error) => {
                this.handleStudySaveError(error);
            },
        });
    }

    approveStudy() {
        if (!this.studyThresholdsFormGroup.valid && !this.studyThresholdsFormGroup.disabled) {
            markFormGroupTouched(this.studyThresholdsFormGroup);
            return;
        }

        const model = transformToCreateUpdateStudyDataModel(this.study);
        model.isActive = true;
        model.isApproved = true;
        model.isFromClinTrak = true;

        const dirtyThresholdRows = this.studyThresholdsFormGroup.timelineFormGroups().filter((r) => r.isDirty());
        if (dirtyThresholdRows.length > 0) {
            model.paymentApprovalThresholds = dirtyThresholdRows.map((r) => ({
                threshold1: r.controls.threshold1.value ?? undefined,
                threshold2: r.controls.threshold2.value ?? undefined,
                effectiveDate: r.controls.effectiveDate.value!,
            }));
        }

        this.studyServices.updateStudy(model.id, model).subscribe({
            next: (response) => {
                this.location.back();
                this.snackbarService.openInfoSnackbar(`Study ${this.study.studyCode} approved successfully`);
            },
            error: (error) => {
                this.handleStudySaveError(error);
            },
        });
    }

    updateExistingStudy(event: StudySaveType, withSites: boolean = false) {
        let model: CreateUpdateStudyDataModel | StudyDataModel;
        if (withSites) {
            //for now needed when sites are added with spreadsheet
            model = transformToStudyDataModel(this.study);
        } else {
            model = transformToCreateUpdateStudyDataModel(this.study);
        }
        // model.isApproved = true; //first update is like approve
        this.studyServices.updateStudy(model.id, model).subscribe({
            next: (response) => {
                //when data changed, we clear the state to ensure all components get the updated data, then set the state again with the updated study data.
                this.studyServicev2.studyOverview.clear();
                let card = this.cardService.getState();
                if (card?.cardForm) {
                    card.cardForm.toggleEditMode();
                    this.cardService.clearCurrentState(card);
                }
                this.setStates(model.id);
                if (event.type !== 'incrementalSave') {
                    this.location.back();
                }

                this.snackbarService.openInfoSnackbar(`Updated study ${this.study.studyCode}`);
            },
            error: (error) => {
                this.handleStudySaveError(error);
            },
        });
    }

    private handleStudySaveError(error) {
        switch (error?.status) {
            case 400:
                switch (error.error.errorReasonCode) {
                    case 'DuplicateStudy':
                        const dialogConfig = new MatDialogConfig();

                        dialogConfig.disableClose = false;
                        dialogConfig.autoFocus = true;
                        dialogConfig.maxWidth = ModalSize.Small;
                        dialogConfig.minWidth = ModalSize.Small;

                        dialogConfig.data = {
                            title: 'Duplicate Entry',
                            bodyText: 'This study cannot be created because a study with the same code already exists.',
                        };

                        this.dialog.open(MessageDialogComponent, dialogConfig);
                        break;
                    default:
                        throw error;
                }
            default:
                throw error;
        }
    }

    private mapClinTrakPerson(p: {
        studyOrganizationPersonRoleId: string;
        firstName: string;
        lastName: string;
        roleType?: string;
        type?: string;
        personTypeName?: string;
        countriesId?: string[];
        regionsId?: string[];
    }): ClinTrakPersonViewModel {
        return {
            Id: 0,
            StudyOrganizationPersonRoleId: p.studyOrganizationPersonRoleId,
            firstName: p.firstName,
            lastName: p.lastName,
            fullName: `${p.firstName} ${p.lastName}`.trim(),
            roleType: p.roleType,
            type: p.type,
            personTypeName: p.personTypeName,
            countriesId: p.countriesId ?? [],
            regionsId: p.regionsId ?? [],
            studyPersonLocales: [],
        };
    }

    private mapClinTrakCountry(c: {
        countryId: string;
        regionId: string;
        countryName?: string;
        regionName?: string;
        isActive: boolean;
    }): CountryRegionCtsmViewModel {
        return {
            countryId: c.countryId,
            regionId: c.regionId,
            countryName: c.countryName,
            regionName: c.regionName,
            isActive: c.isActive,
        };
    }
}
